import type { ReactElement, VoidFunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import type { SelectCallback } from 'react-bootstrap';
import { connect, useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import type { Role } from '../../../../../shared/model-typings';
import { Action, ResourceType } from '../../../../../typings/permissions';
import { window } from '../../../../globals';
import { useOnMount } from '../../../../hooks';
import OrganizationAccessRequestsModule from '../../../../redux/organization_access_requests';
import type { RolesStore } from '../../../../redux/roles';
import RolesModules from '../../../../redux/roles';
import TeamsModule from '../../../../redux/teams';
import type { ReduxStore } from '../../../../redux/store';
import type { UsersStore } from '../../../../redux/users';
import UsersModule from '../../../../redux/users';
import OrganizationsModule from '../../../../redux/organizations';
import type { AsyncStatus, Organization, OrganizationAccessRequest } from '../../../../types';
import Tabs from '../../../generic/Tabs';
import ModalInviteUsers from '../../../modals/ModalInviteUsers';
import Header from '../../../shared/settings/SettingsPageHeader';
import OrganizationAccessRequestsTable from './OrganizationAccessRequestsTable';
import UserInvitationsTable from './UserInvitationsTable';
import UsersTable from './UsersTable';
import { NONE_ROLE } from './constants';
import Loader from '../../../generic/Loader';
import { useUserInvitations, useCreateUserInvitations } from '../../../../hooks/api';
import SettingsPageTableActions from '../../../shared/settings/SettingsPageTableActions';
import { useHasPermission } from '../../../../shared/permissions';

type Tab = 'members' | 'requests' | 'invites';

type OwnProps = {
  organization: Organization;
};

type ReduxStateProps = {
  users: UsersStore['users'];
  organizationAccessRequests: Record<string, OrganizationAccessRequest>;
  roles: RolesStore['roles'];
};

type DispatchProps = {
  inviteExistingAccessRequest: (organizationId: number, accessRequestId: number) => void;
  removeAccessRequest: (organizationId: number, accessRequestId: number) => void;
};

type OrganizationUsersIndexProps = OwnProps & ReduxStateProps & DispatchProps;

const OrganizationUsersIndex = ({
  users,
  roles,
  inviteExistingAccessRequest,
  organization,
  organizationAccessRequests,
  removeAccessRequest,
}: OrganizationUsersIndexProps) => {
  const hasPermission = useHasPermission();
  const [searchValue, setSearchValue] = useState('');
  const [shouldShowInviteModal, setShouldShowInviteModal] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('members');
  const [orgRoleFilter, setOrgRoleFilter] = useState<Role | 'None' | null>(null);
  const { data: userInvitations, isLoading, isError, error } = useUserInvitations();
  //  Note: this should probably live in ModalInviteUsers, but that is a class component.
  const {
    mutateAsync: createUserInvitations,
    error: createInviteError,
    isLoading: isCreateInviteIsLoading,
  } = useCreateUserInvitations(organization.id);

  const onSetActiveTabFn = (tab: Tab) => () => {
    setActiveTab(tab);
    setSearchValue('');
  };

  const canInviteUser = hasPermission({ action: Action.Invite, resourceType: ResourceType.User });

  //  Handle changing the role filter dropdown.
  const onRoleFilterDropdownSelect = (roleId: number | 'None' | 'clear') => {
    //  'None' is for users who have no org role, `null` is for when users clear the Dropdown.
    if (roleId === NONE_ROLE || roleId === 'clear') {
      return setOrgRoleFilter(roleId === 'clear' ? null : roleId);
    }
    const selectedRole = Object.values(roles).find(role => role.id === roleId);
    return setOrgRoleFilter(selectedRole || null);
  };

  let body: ReactElement;
  switch (activeTab) {
    case 'requests':
      body = (
        <OrganizationAccessRequestsTable
          accessRequests={organizationAccessRequests}
          searchQuery={searchValue}
          onAcceptRequest={requestId => inviteExistingAccessRequest(organization.id, requestId)}
          onIgnoreRequest={requestId => removeAccessRequest(organization.id, requestId)}
      />
      );
      break;
    case 'invites':
      body = (
        <UserInvitationsTable
          invitations={userInvitations ?? []}
          users={users}
          searchQuery={searchValue}
      />
      );
      break;
    case 'members':
    default:
      body = (
        <UsersTable
          users={users}
          searchQuery={searchValue}
          orgRoleFilter={orgRoleFilter}
      />
      );
  }

  const shouldShowTabs = window.on_prem || (canInviteUser && !organization.disable_invite);
  const orgRoles = Object.values(roles).filter(role => role.scope === 'organization');

  return (
    <>
      <Header heading="Users">
        {isError && (
          <Header.Errors errors={[error.message]} />
        )}
      </Header>
      {shouldShowTabs && (
        <Tabs>
          <Tabs.Tab
            label="Members"
            isActive={activeTab === 'members'}
            onSelect={onSetActiveTabFn('members')}
            badgeCount={Object.values(users).length}
          />
          {window.on_prem && (
            <Tabs.Tab
              label="Requests"
              isActive={activeTab === 'requests'}
              onSelect={onSetActiveTabFn('requests')}
              badgeCount={Object.values(organizationAccessRequests).length}
            />
          )}
          {canInviteUser && !organization.disable_invite && (
            <Tabs.Tab
              label="Invites"
              isActive={activeTab === 'invites'}
              onSelect={onSetActiveTabFn('invites')}
              badgeCount={isLoading ? 0 : userInvitations?.length ?? 0}
            />
          )}
        </Tabs>
      )}
      <SettingsPageTableActions>
        <SettingsPageTableActions.TooltipButton
          id="invite-users-button"
          size="large"
          type="secondary"
          onClick={() => setShouldShowInviteModal(true)}
          isDisabled={!canInviteUser}
          showTooltip={!canInviteUser}
          tooltip="You are not permitted to invite users."
        >
          Invite Users
        </SettingsPageTableActions.TooltipButton>
        <SettingsPageTableActions.DropdownButton
          id="user-roles-filter"
          title={orgRoleFilter === NONE_ROLE ? NONE_ROLE : orgRoleFilter?.name || 'Organization Role'}
          onSelect={onRoleFilterDropdownSelect as SelectCallback}
          size="large"
        >
          <SettingsPageTableActions.DropdownButton.Item eventKey="clear">
            Clear Filter
          </SettingsPageTableActions.DropdownButton.Item>
          {orgRoles.map(role => (
            <SettingsPageTableActions.DropdownButton.Item key={role.id} eventKey={role.id}>
              {role.name}
            </SettingsPageTableActions.DropdownButton.Item>
          ))}
        </SettingsPageTableActions.DropdownButton>
        <SettingsPageTableActions.SearchInput
          label="Search users"
          size="medium"
          placeholder="Search users"
          value={searchValue}
          onChange={setSearchValue}
        />
      </SettingsPageTableActions>
      {body}
      <ModalInviteUsers
        show={shouldShowInviteModal}
        onCreateUserInvitations={createUserInvitations as any as (emails: Set<string>) => Promise<void>}
        createInviteError={createInviteError?.data?.message}
        createInviteIsLoading={isCreateInviteIsLoading}
        onHide={() => {
          setShouldShowInviteModal(false);
        }}
      />
    </>
  );
};

const mapStateToProps = createStructuredSelector<ReduxStore, ReduxStateProps>({
  users: UsersModule.selectors.getUsersByOrganization,
  organizationAccessRequests: OrganizationAccessRequestsModule.selectors.getActiveRequestsForOrganization,
  roles: RolesModules.selectors.getRoles,
});

const mapDispatchToProps: DispatchProps = {
  inviteExistingAccessRequest: OrganizationAccessRequestsModule.actions.inviteExisting,
  removeAccessRequest: OrganizationAccessRequestsModule.actions.removeAccessRequest,
};

const ConnectedOrganizationUsersIndex = connect(
  mapStateToProps,
  mapDispatchToProps,
)(OrganizationUsersIndex);

/**
 * Container component is required for ensuring that organization is fetched before
 * the OrganizationUsersIndex is rendered, because `useUserInvitations` (and other places)
 * in the component rely on Organization being present.
 */
const OrganizationUsersIndexContainer: VoidFunctionComponent<ReduxStateProps & DispatchProps> = (props) => {
  const dispatch = useDispatch();
  const organization = useSelector(OrganizationsModule.selectors.getCurrentOrganization);
  const organizationStatus = useSelector(OrganizationsModule.selectors.getStatus);
  const usersStatus = useSelector(UsersModule.selectors.getStatus) as AsyncStatus;

  useOnMount(async () => {
    dispatch(OrganizationsModule.actions.fetchOne(window.user?.organizationId as number));
    dispatch(UsersModule.actions.fetchUsersByOrganization(window.user?.organizationId as number));
    //  Needed to hydrate the Role column/filter
    dispatch(RolesModules.actions.fetchAll());
    //  Needed to hydrate the Teams column
    dispatch(TeamsModule.actions.fetchAll());
  });

  useEffect(() => {
    if (organization) {
      dispatch(UsersModule.actions.extractFromOrganization(organization));
      dispatch(OrganizationAccessRequestsModule.actions.extractFromOrganization(organization));
    }
  }, [dispatch, organization]);

  if (!organization || usersStatus.isLoading || organizationStatus.isLoading) {
    return (
      <div className="padding-top-lg">
        <Loader size={80} />
      </div>
    );
  }

  return (
    <ConnectedOrganizationUsersIndex
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      organization={organization}
    />
  );
};

export default OrganizationUsersIndexContainer;
